import type { HttpPost, HttpRequest } from '@/shared/infra/http'
import { HttpError, mapServerError } from '@/shared/infra/http'
import type { RegistrationFieldIssue } from '@/modules/auth/domain'
import { RegistrationFieldError } from '@/modules/auth/domain'
import {
  AccountExistsError,
  ComplianceIncompleteError,
  ContactClaimRequiredError,
  DuplicateContactError,
  IdentityMergeConflictError,
  PlaceholderMergeLedgerConflictError,
} from '@/shared/domain'
import type {
  CompleteRegistrationFromInvitation,
  CompleteRegistrationFromInvitationParams,
  CompleteRegistrationFromInvitationResult,
} from '@/modules/auth/use-cases'

type RawUser = {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  blindbookId?: string | null
}

type RawMembership = {
  organizationId?: string
  role?: string
  status?: string
  requiresPro?: boolean
}

type RawSuccessResponse = {
  success?: boolean
  user?: RawUser
  membership?: RawMembership
  requiresPro?: boolean
}

type RawComplianceErrorEntry = { field?: string; message?: string }

type RawConflictData = {
  reason?: string
  identifier?: string
  identifierType?: string
  pendingInvite?: boolean
  holderUnregistered?: boolean
}

type RawErrorBody = {
  ok?: false
  error?: string
  code?: string
  errors?: RawComplianceErrorEntry[]
  data?: RawConflictData
}

export class EdgeCompleteRegistrationRepository {
  constructor(private readonly httpClient: HttpPost) {}

  execute: CompleteRegistrationFromInvitation = async (
    params: CompleteRegistrationFromInvitationParams,
  ): Promise<CompleteRegistrationFromInvitationResult> => {
    try {
      const request: HttpRequest<CompleteRegistrationFromInvitationParams> = {
        url: '/users/complete-registration',
        body: params,
      }
      const response = await this.httpClient.post<
        CompleteRegistrationFromInvitationParams,
        RawSuccessResponse
      >(request)
      return mapSuccessResponse(response)
    } catch (error) {
      if (error instanceof HttpError && error.status === 422) {
        const body = (error.body ?? {}) as RawErrorBody
        const missingFields = Array.isArray(body.errors)
          ? body.errors
              .map((entry) => entry.field)
              .filter((value): value is string => typeof value === 'string')
          : []
        throw new ComplianceIncompleteError(missingFields, body.error)
      }
      if (error instanceof HttpError && error.status === 409) {
        throwConflictError((error.body ?? {}) as RawErrorBody)
      }
      if (error instanceof HttpError && error.status === 400) {
        throwFieldError(error, (error.body ?? {}) as RawErrorBody)
      }
      throw error
    }
  }
}

function throwFieldError(error: HttpError, body: RawErrorBody): never {
  const issues: RegistrationFieldIssue[] = Array.isArray(body.errors)
    ? body.errors
        .filter((entry): entry is Required<RawComplianceErrorEntry> =>
          typeof entry.field === 'string' && entry.field.length > 0,
        )
        .map((entry) => ({
          field: entry.field,
          message:
            typeof entry.message === 'string' && entry.message.length > 0
              ? entry.message
              : (body.error ?? 'This value is not valid.'),
        }))
    : []

  if (issues.length > 0) {
    throw new RegistrationFieldError(issues, body.error)
  }

  mapServerError(error)
}

function throwConflictError(body: RawErrorBody): never {
  if (body.code === 'IDENTITY_MERGE_CONFLICT') {
    throw new IdentityMergeConflictError(body.error)
  }
  if (body.code === 'placeholder_merge_ledger_conflict') {
    throw new PlaceholderMergeLedgerConflictError()
  }
  const data = body.data ?? {}
  if (
    data.reason === 'contact_claim_required' &&
    typeof data.identifier === 'string' &&
    (data.identifierType === 'email' || data.identifierType === 'phone')
  ) {
    throw new ContactClaimRequiredError(
      data.identifier,
      data.identifierType,
      data.pendingInvite === true,
      data.holderUnregistered === true,
    )
  }
  if (data.reason === 'account_exists' && typeof data.identifier === 'string') {
    throw new AccountExistsError(
      data.identifier,
      data.identifierType === 'phone' ? 'phone' : 'email',
    )
  }
  throw new DuplicateContactError(body.error)
}

function mapSuccessResponse(
  response: RawSuccessResponse,
): CompleteRegistrationFromInvitationResult {
  const raw = response ?? {}
  const user = raw.user ?? {}
  const membership = raw.membership ?? {}
  const requiresPro = membership.requiresPro === true || raw.requiresPro === true

  return {
    user: {
      id: user.id ?? '',
      email: user.email ?? '',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      blindbookId: user.blindbookId ?? undefined,
    },
    membership: {
      organizationId: membership.organizationId ?? '',
      role: membership.role ?? '',
      status: membership.status ?? '',
    },
    ...(requiresPro ? { requiresPro: true } : {}),
  }
}
